import ArrowIcon from '@/assets/icons/Arrow';
import NavigationLink from '@/components/NavigationLink';

import { cn } from '@/lib/utils';
import isOuinexMarketDomain from '@/utils/isOuinexMarketDomain';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as React from 'react';

const buttonVariants = cva(
  'min-w-max relative isolate inline-flex items-center justify-center rounded-full font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 select-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary-900 active:bg-primary/50',
        outline:
          'border border-current bg-transparent hover:after:size-full after:bg-current after:absolute after:-z-10 after:opacity-10 active:after:opacity-5',
        secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary-900 active:bg-secondary/50',
        ghost: 'hover:bg-white/10 hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 underline rounded-none',
        text: 'text-current rounded-none'
      },
      color: {
        primary: 'text-primary',
        secondary: 'text-secondary',
        light: 'text-white',
        gray: 'text-zinc-300'
      },
      size: {
        default: 'text-sm md:text-base py-[0.3em] px-[1.25em]',
        sm: 'text-sm py-[0.3em] px-[1em]',
        md: 'text-base md:text-lg py-[0.375em] px-[1.5em]',
        lg: 'text-base md:text-lg lg:text-xl py-[0.5em] px-[1.5em]',
        icon: 'h-9 w-9'
      }
    },
    compoundVariants: [
      {
        variant: 'link',
        className: 'w-fit p-0'
      },
      {
        variant: 'text',
        className: 'w-fit p-0'
      }
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      color,
      children,
      external = false,
      seoLabel,
      url,
      href,
      label,
      asChild = false,
      ...props
    },
    ref
  ) => {
    const _href = href || url;
    let Comp = external || _href ? NavigationLink : asChild ? Slot : 'button';
    let extraProps = (external || _href) && _href ? { href: _href } : {};

    if (external) extraProps['target'] = '_blank';

    if (!label && !children) return <></>;

    const _className = cn(buttonVariants({ variant, size, color, className }));

    // this will be removed later
    if (isOuinexMarketDomain(_href)) {
      extraProps = {
        'data-url': _href
      };
      Comp = 'button';
    }

    return (
      <Comp className={_className} ref={ref} {...props} {...extraProps}>
        {children || <div className='btn-label max-w-[calc(100vw-6rem)] truncate'>{label}</div>}
        {external && <ArrowIcon className='size-3.5 ml-2' />}
        {seoLabel && <span className='sr-only'>{seoLabel}</span>}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
